import { Injectable, Injector } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import {
  Action,
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  DocumentChangeAction,
  DocumentSnapshotDoesNotExist,
  DocumentSnapshotExists,
} from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Observable, from, throwError } from 'rxjs';
import { catchError, first, map, switchMap, take, tap } from 'rxjs/operators';
import { FirestoreOp } from '../models/firestore-telemetry.model';
import { PermissionDeniedReporter } from './telemetry/permission-denied-reporter.service';

type CollectionPredicate<T> = string | AngularFirestoreCollection<T>;
type DocPredicate<T> = string | AngularFirestoreDocument<T>;

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  constructor(private asf: AngularFirestore, private auth: AngularFireAuth, private injector: Injector) {}

  col<T>(ref: CollectionPredicate<T>, queryFn?): AngularFirestoreCollection<T> {
    return typeof ref === 'string' ? this.asf.collection<T>(ref, queryFn) : ref;
  }

  // Resolves once every write queued so far is acknowledged by the server, or rejects after timeoutMs.
  waitForPendingWrites(timeoutMs: number): Promise<void> {
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('Timed out waiting for pending Firestore writes')), timeoutMs);
    });
    return Promise.race([this.asf.firestore.waitForPendingWrites(), timeout]).finally(() => clearTimeout(timer));
  }

  doc<T>(ref: DocPredicate<T>): AngularFirestoreDocument<T> {
    return typeof ref === 'string' ? this.asf.doc<T>(ref) : ref;
  }

  doc$<T>(ref: DocPredicate<T>): Observable<T> {
    return this.doc(ref)
      .snapshotChanges()
      .pipe(
        map((doc: Action<DocumentSnapshotDoesNotExist | DocumentSnapshotExists<T>>) => doc.payload.data() as T),
        catchError(this.reportObservable(ref, 'listen'))
      );
  }

  col$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<T[]> {
    return this.col(ref, queryFn)
      .snapshotChanges()
      .pipe(
        map((docs: DocumentChangeAction<T>[]) => docs.map((a: DocumentChangeAction<T>) => a.payload.doc.data()) as T[]),
        catchError(this.reportObservable(ref, 'listen'))
      );
  }

  colWithIds$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<any[]> {
    return this.col(ref, queryFn)
      .snapshotChanges()
      .pipe(
        map((actions: DocumentChangeAction<T>[]) =>
          actions.map((a: DocumentChangeAction<T>) => {
            const data = a.payload.doc.data() as T;
            const id = a.payload.doc.id;
            const doc = a.payload.doc;
            return { ...data, id, doc };
          })
        ),
        catchError(this.reportObservable(ref, 'listen'))
      );
  }

  /**
   * One-shot read with document ids — same row shape as `colWithIds$`, but it does NOT open a listener.
   * Use this instead of `colWithIds$().pipe(take(1))`: `snapshotChanges()` is latency-compensated, so its
   * FIRST emission is served from the local cache (`metadata.fromCache === true`) and may be an incomplete
   * subset; the complete server snapshot only arrives on a later emission. `take(1)` would freeze that
   * partial view and tear the listener down before the server data lands. `get()` resolves against the
   * server (falling back to cache only when offline) and completes, so there is a single, complete result.
   */
  colWithIdsOnce$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<any[]> {
    return this.col(ref, queryFn)
      .get()
      .pipe(
        map((snapshot: firebase.firestore.QuerySnapshot<T>) =>
          snapshot.docs.map((doc) => ({ ...(doc.data() as T), id: doc.id, doc }))
        ),
        catchError(this.reportObservable(ref, 'list'))
      );
  }

  queryCollectionWithChunks$(collection: string, queryRef, field: string, values: string[], chunkSize = 8, orderBy?: string): Observable<any[]> {
    const chunks = [];

    for (let i = 0; i < values.length; i += chunkSize) {
      const chunk = values.slice(i, i + chunkSize);
      const completeQuery = this.asf.collection(collection, (ref) => {
        let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
        query = queryRef(query);
        query = query.where(field, 'in', chunk);
        query = query.orderBy(orderBy);
        return query;
      });
      chunks.push(completeQuery);
    }

    const snapshotPromises = chunks.map((query) => query.get().pipe(take(1)).toPromise());
    const snapshots$ = from(Promise.all(snapshotPromises));

    return snapshots$.pipe(
      map((snapshots) => {
        const results = [];
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((doc) => {
            results.push(doc.data());
          });
        });
        return results;
      })
    );
  }

  /// Firebase Server Timestamp
  get timestamp() {
    return firebase.firestore.FieldValue.serverTimestamp();
  }

  async set<T>(ref: DocPredicate<T>, data: any): Promise<void> {
    const timestamp = this.timestamp;
    const user = await this.auth.currentUser;
    return this.doc(ref)
      .set({
        ...data,
        updatedAt: timestamp,
        createdAt: timestamp,
        createdBy: user.uid,
        lastUpdatedBy: user.uid,
      })
      .catch(this.reportPromise(ref, 'set'));
  }

  update<T>(ref: DocPredicate<T>, data: any): Promise<any> {
    return this.auth.user
      .pipe(
        first(),
        switchMap((user) =>
          this.doc(ref).update({
            ...data,
            lastUpdatedBy: user.uid,
            updatedAt: this.timestamp,
          })
        )
      )
      .toPromise()
      .catch(this.reportPromise(ref, 'update'));
  }

  delete<T>(ref: DocPredicate<T>): Promise<void> {
    return this.doc(ref).delete().catch(this.reportPromise(ref, 'delete'));
  }

  async add<T>(ref: CollectionPredicate<T>, data): Promise<firebase.firestore.DocumentReference> {
    const timestamp = this.timestamp;
    const user = await this.auth.currentUser;
    return this.col(ref)
      .add({
        ...data,
        updatedAt: timestamp,
        createdAt: timestamp,
        createdBy: user.uid,
        lastUpdatedBy: user.uid,
      })
      .catch(this.reportPromise(ref, 'create'));
  }

  upsert<T>(ref: DocPredicate<T>, data: any): Promise<void> {
    const doc = this.doc(ref).snapshotChanges().pipe(take(1)).toPromise();

    return doc.then((snap: Action<DocumentSnapshotDoesNotExist | DocumentSnapshotExists<T>>) => (snap.payload.exists ? this.update(ref, data) : this.set(ref, data)));
  }

  // Sometimes I just want to see what I’m working with.
  // It seems silly to re-import RxJS operators and subscribe to data every time I want to do this.
  // I also wrapped the operation with a timer so you can check the latency for a given query.

  inspectDoc(ref: DocPredicate<any>): void {
    const tick = new Date().getTime();
    this.doc(ref)
      .snapshotChanges()
      .pipe(
        take(1),
        tap((d: Action<DocumentSnapshotDoesNotExist | DocumentSnapshotExists<any>>) => {
          const tock = new Date().getTime() - tick;
        })
      )
      .subscribe();
  }

  inspectCol(ref: CollectionPredicate<any>): void {
    const tick = new Date().getTime();
    this.col(ref)
      .snapshotChanges()
      .pipe(
        take(1),
        tap((c: DocumentChangeAction<any>[]) => {
          const tock = new Date().getTime() - tick;
        })
      )
      .subscribe();
  }

  createId(): string {
    return this.asf.createId();
  }

  batch() {
    return this.asf.firestore.batch();
  }

  getDb() {
    return this.asf;
  }

  // Reports a rules denial on an observable read, then rethrows it unchanged.
  private reportObservable(ref: CollectionPredicate<any> | DocPredicate<any>, op: FirestoreOp) {
    return (error: unknown) => {
      this.reportDenied(ref, op, error);
      return throwError(() => error);
    };
  }

  // Reports a rules denial on a write, then rethrows it unchanged.
  private reportPromise(ref: CollectionPredicate<any> | DocPredicate<any>, op: FirestoreOp) {
    return (error: unknown): never => {
      this.reportDenied(ref, op, error);
      throw error;
    };
  }

  // Resolved lazily to keep FirestoreService free of the HttpClient/Router dependency chain.
  private reportDenied(ref: CollectionPredicate<any> | DocPredicate<any>, op: FirestoreOp, error: unknown): void {
    try {
      const path = typeof ref === 'string' ? ref : (ref as any)?.ref?.path;
      this.injector.get(PermissionDeniedReporter).report(path, op, error);
    } catch {
      // Telemetry must never change the caller's error.
    }
  }
}
